import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Settings, Save, Euro, Shield, CreditCard, Percent, FileText, Upload, CheckCircle2, Loader2, Zap, Building2, Image, Globe, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const SettingsPage = () => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingContract, setUploadingContract] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [contractTemplate, setContractTemplate] = useState(null);
  const [settings, setSettings] = useState({
    // Payment settings
    booking_deposit_percentage: 20,
    stripe_fee_percentage: 3,        // legacy
    stripe_eu_fee_percentage: 1.5,
    stripe_intl_fee_percentage: 2.5,
    stripe_fixed_fee: 0.25,
    bank_transfer_fee: 0,
    
    // Security deposit settings
    short_stay_security_deposit: 300,
    long_stay_threshold_days: 60,

    // Date from which NEW long-term bookings pay the card fee on monthly rent.
    // Blank = no long-term booking is ever charged it (the gate fails closed),
    // so this must be saved, not merely displayed.
    monthly_card_fee_since: '',

    // Night check-in fee (22:00–08:00 arrival, guest-requested)
    night_checkin_fee: 50,
    
    // Electricity & Agent Fee
    electricity_price_per_kwh: 0.15,
    agent_fee_percentage: 20,
    
    // Tax settings
    invoice_tax_rate: 21,
    
    // Billing details
    company_name: '',
    company_logo: '',
    company_vat_id: '',
    company_address: '',
    company_email: '',
    company_phone: '',
    
    // Currency
    default_currency: 'EUR'
  });

  const isAdmin = user?.role === 'ADMIN';

  useEffect(() => {
    fetchSettings();
    fetchContractTemplate();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      // Settings might not exist yet, use defaults
      console.log('Using default settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchContractTemplate = async () => {
    try {
      const response = await axios.get(`${API}/contracts/template/active`);
      if (response.data?.has_template) {
        setContractTemplate(response.data.template);
      }
    } catch (error) {
      console.log('No contract template found');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file (PNG, JPG, etc.)');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo file size must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Logo = e.target.result;
        setSettings(prev => ({ ...prev, company_logo: base64Logo }));
        setUploadingLogo(false);
        toast.success('Logo uploaded - remember to save settings');
      };
      reader.onerror = () => {
        toast.error('Failed to read logo file');
        setUploadingLogo(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload logo');
      setUploadingLogo(false);
    } finally {
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  const handleContractUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.docx')) {
      toast.error('Only Word documents (.docx) are supported');
      return;
    }

    setUploadingContract(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/contracts/template/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setContractTemplate(response.data);
      toast.success('Contract template uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload contract template');
    } finally {
      setUploadingContract(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-red-400" />
          <h2 className="text-xl font-semibold text-[#0c2431]">Access Denied</h2>
          <p className="text-[#194a50]/60">Only administrators can access settings</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  // Contract fields that can be used in templates
  const contractFields = [
    { field: '[DATE]', description: 'Current date when contract is signed' },
    { field: '[OWNER NAME]', description: 'Property owner name' },
    { field: '[TENANT NAME]', description: 'Guest/tenant full name' },
    { field: '[TENANT ID NUMBER]', description: 'Guest passport/ID number' },
    { field: '[FULL ADDRESS OF PROPERTY RENTED]', description: 'Complete property address' },
    { field: '[BOOKIN START DATE]', description: 'Check-in date' },
    { field: '[BOOKING END DATE]', description: 'Check-out date' },
    { field: '[BOOKING TOTAL]', description: 'Total booking amount' },
    { field: '[SECURITY DEPOSIT]', description: 'Security deposit amount' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#0c2431] font-['Outfit']">Settings</h1>
          <p className="text-[#194a50]/70 mt-1">Configure global platform settings</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-[#194a50] hover:bg-[#10555e]"
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      <Tabs defaultValue="payments" className="w-full">
        <TabsList className="grid w-full grid-cols-5 max-w-3xl">
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger value="deposits">Deposits</TabsTrigger>
          <TabsTrigger value="owner">Owner Payouts</TabsTrigger>
          <TabsTrigger value="billing">Billing Info</TabsTrigger>
          <TabsTrigger value="contracts">Contracts</TabsTrigger>
        </TabsList>

        {/* Payments & Fees Tab */}
        <TabsContent value="payments" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Booking Deposit */}
            <Card className="shadow-sm border-[#194a50]/10">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Percent className="w-5 h-5 text-[#194a50]" />
                  Booking Deposit
                </CardTitle>
                <CardDescription>
                  Percentage of total booking amount required to confirm reservation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label>Deposit Percentage (%)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={settings.booking_deposit_percentage}
                        onChange={(e) => setSettings({
                          ...settings,
                          booking_deposit_percentage: parseFloat(e.target.value) || 0
                        })}
                        className="w-24"
                        data-testid="deposit-percentage-input"
                      />
                      <span className="text-[#194a50]/60">% of total booking</span>
                    </div>
                    <p className="text-xs text-[#194a50]/50 mt-2">
                      Currently: {settings.booking_deposit_percentage}% deposit required to reserve
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stripe Fee Configuration */}
            <Card className="shadow-sm border-[#194a50]/10">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#194a50]" />
                  Stripe Card Fees
                </CardTitle>
                <CardDescription>
                  Stripe's fee is passed to the customer via gross-up so you always receive the full booking amount.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-5">
                  {/* Fixed fee per transaction */}
                  <div>
                    <Label className="flex items-center gap-1">
                      Fixed Fee per Transaction (€)
                    </Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Euro className="w-4 h-4 text-[#194a50]/50" />
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={settings.stripe_fixed_fee}
                        onChange={(e) => setSettings({ ...settings, stripe_fixed_fee: parseFloat(e.target.value) || 0 })}
                        className="w-24"
                        data-testid="stripe-fixed-fee-input"
                      />
                      <span className="text-[#194a50]/60">€ per charge (Stripe charges €0.25)</span>
                    </div>
                  </div>

                  {/* European cards */}
                  <div className="border rounded-lg p-3 space-y-2 bg-blue-50/40">
                    <p className="text-sm font-medium text-[#0c2431] flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5" /> European Cards
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="10"
                        step="0.1"
                        value={settings.stripe_eu_fee_percentage}
                        onChange={(e) => setSettings({ ...settings, stripe_eu_fee_percentage: parseFloat(e.target.value) || 0 })}
                        className="w-24"
                        data-testid="stripe-eu-fee-input"
                      />
                      <span className="text-[#194a50]/60 text-sm">% + €{settings.stripe_fixed_fee} per transaction</span>
                    </div>
                    <p className="text-xs text-[#194a50]/50">
                      Customer charge on €100: <strong className="text-[#0c2431]">€{(100 + ((100 * settings.stripe_eu_fee_percentage / 100) + settings.stripe_fixed_fee) / (1 - settings.stripe_eu_fee_percentage / 100)).toFixed(2)}</strong>
                      {' '}→ fee: <strong className="text-[#0c2431]">€{(((100 * settings.stripe_eu_fee_percentage / 100) + settings.stripe_fixed_fee) / (1 - settings.stripe_eu_fee_percentage / 100)).toFixed(2)}</strong>
                    </p>
                  </div>

                  {/* International cards */}
                  <div className="border rounded-lg p-3 space-y-2 bg-orange-50/40">
                    <p className="text-sm font-medium text-[#0c2431] flex items-center gap-1.5">
                      <Globe className="w-3.5 h-3.5" /> International Cards
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="10"
                        step="0.1"
                        value={settings.stripe_intl_fee_percentage}
                        onChange={(e) => setSettings({ ...settings, stripe_intl_fee_percentage: parseFloat(e.target.value) || 0 })}
                        className="w-24"
                        data-testid="stripe-intl-fee-input"
                      />
                      <span className="text-[#194a50]/60 text-sm">% + €{settings.stripe_fixed_fee} per transaction</span>
                    </div>
                    <p className="text-xs text-[#194a50]/50">
                      Customer charge on €100: <strong className="text-[#0c2431]">€{(100 + ((100 * settings.stripe_intl_fee_percentage / 100) + settings.stripe_fixed_fee) / (1 - settings.stripe_intl_fee_percentage / 100)).toFixed(2)}</strong>
                      {' '}→ fee: <strong className="text-[#0c2431]">€{(((100 * settings.stripe_intl_fee_percentage / 100) + settings.stripe_fixed_fee) / (1 - settings.stripe_intl_fee_percentage / 100)).toFixed(2)}</strong>
                    </p>
                  </div>

                  {/* Bank Transfer */}
                  <div>
                    <Label>Bank Transfer Fee (€)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={settings.bank_transfer_fee}
                        onChange={(e) => setSettings({ ...settings, bank_transfer_fee: parseFloat(e.target.value) || 0 })}
                        className="w-24"
                        data-testid="bank-transfer-fee-input"
                      />
                      <span className="text-[#194a50]/60">€ fixed fee (currently free)</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Security Deposits Tab */}
        <TabsContent value="deposits" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Night Check-in Fee */}
            <Card className="shadow-sm border-[#194a50]/10">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#c8724a]" />
                  Night Check-in Fee
                </CardTitle>
                <CardDescription>
                  Added when a guest requests arrival between 22:00 and 08:00. Shown on the booking form and charged as its own line item at checkout.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label>Night Check-in Fee (€)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Euro className="w-4 h-4 text-[#194a50]/50" />
                      <Input
                        type="number"
                        min="0"
                        step="5"
                        value={settings.night_checkin_fee}
                        onChange={(e) => setSettings({
                          ...settings,
                          night_checkin_fee: parseFloat(e.target.value) || 0
                        })}
                        className="w-32"
                        data-testid="night-checkin-fee-input"
                      />
                    </div>
                    <p className="text-xs text-[#194a50]/50 mt-2">
                      Set to 0 to disable. Guaranteed check-in window is 16:00–22:00.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Short Stay Security Deposit */}
            <Card className="shadow-sm border-[#194a50]/10">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#194a50]" />
                  Short Stay Security Deposit
                </CardTitle>
                <CardDescription>
                  Fixed amount held as damage protection for stays under {settings.long_stay_threshold_days} days
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label>Security Deposit Amount (€)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Euro className="w-4 h-4 text-[#194a50]/50" />
                      <Input
                        type="number"
                        min="0"
                        step="50"
                        value={settings.short_stay_security_deposit}
                        onChange={(e) => setSettings({
                          ...settings,
                          short_stay_security_deposit: parseFloat(e.target.value) || 0
                        })}
                        className="w-32"
                        data-testid="short-stay-deposit-input"
                      />
                    </div>
                    <p className="text-xs text-[#194a50]/50 mt-2">
                      Applied to bookings shorter than {settings.long_stay_threshold_days} days
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Long Stay Security Deposit */}
            <Card className="shadow-sm border-[#194a50]/10">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#d8a28c]" />
                  Long Stay Security Deposit
                </CardTitle>
                <CardDescription>
                  For stays of {settings.long_stay_threshold_days}+ days, security deposit equals one month's average rate
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label>Long Stay Threshold (days)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        min="30"
                        max="365"
                        value={settings.long_stay_threshold_days}
                        onChange={(e) => setSettings({
                          ...settings,
                          long_stay_threshold_days: parseInt(e.target.value) || 90
                        })}
                        className="w-24"
                        data-testid="long-stay-threshold-input"
                      />
                      <span className="text-[#194a50]/60">days</span>
                    </div>
                    <p className="text-xs text-[#194a50]/50 mt-2">
                      Bookings ≥ this duration use monthly rate as security deposit
                    </p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong>Calculation:</strong> (Total Booking Amount ÷ Number of Nights) × 30 days
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Monthly Card Fee Passthrough */}
            <Card className="shadow-sm border-[#194a50]/10">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Euro className="w-5 h-5 text-[#194a50]" />
                  Monthly Card Fee Passthrough
                </CardTitle>
                <CardDescription>
                  Long-term bookings created on or after this date pay the card fee on
                  monthly rent; earlier ones keep their agreed price and the fee is
                  recovered on the owner invoice
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label>Passthrough Live From</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="date"
                        value={settings.monthly_card_fee_since || ''}
                        onChange={(e) => setSettings({
                          ...settings,
                          monthly_card_fee_since: e.target.value
                        })}
                        className="w-44"
                        data-testid="monthly-card-fee-since-input"
                      />
                    </div>
                    <p className="text-xs text-[#194a50]/50 mt-2">
                      Per-booking overrides on the booking page win over this date
                    </p>
                  </div>
                  {!settings.monthly_card_fee_since && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-sm text-amber-800">
                        <strong>Empty:</strong> no long-term booking is charged the card
                        fee on monthly rent, from month 1 on, unless it has a per-booking
                        override. Set a date and save.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Owner Payouts Tab */}
        <TabsContent value="owner" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Electricity Pricing */}
            <Card className="shadow-sm border-[#194a50]/10">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  Electricity Pricing
                </CardTitle>
                <CardDescription>
                  Price per kW/h charged to guests and passed to owners
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label>Price per kW/h (€)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Euro className="w-4 h-4 text-[#194a50]/50" />
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={settings.electricity_price_per_kwh}
                        onChange={(e) => setSettings({
                          ...settings,
                          electricity_price_per_kwh: parseFloat(e.target.value) || 0
                        })}
                        className="w-32"
                        data-testid="electricity-price-input"
                      />
                      <span className="text-[#194a50]/60">€ per kW/h</span>
                    </div>
                    <p className="text-xs text-[#194a50]/50 mt-2">
                      Electricity consumption = (Check-out kW/h - Check-in kW/h) × Price
                    </p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> Electricity is typically deducted from the security deposit after check-out.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Agent Fee */}
            <Card className="shadow-sm border-[#194a50]/10">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Percent className="w-5 h-5 text-[#194a50]" />
                  Agent Commission
                </CardTitle>
                <CardDescription>
                  Percentage deducted from owner payouts for management services
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label>Agent Fee (%)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={settings.agent_fee_percentage}
                        onChange={(e) => setSettings({
                          ...settings,
                          agent_fee_percentage: parseFloat(e.target.value) || 0
                        })}
                        className="w-24"
                        data-testid="agent-fee-input"
                      />
                      <span className="text-[#194a50]/60">% of accommodation total</span>
                    </div>
                  </div>
                  <div>
                    <Label>Invoice Tax Rate (%)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        min="0"
                        max="50"
                        step="0.5"
                        value={settings.invoice_tax_rate}
                        onChange={(e) => setSettings({
                          ...settings,
                          invoice_tax_rate: parseFloat(e.target.value) || 0
                        })}
                        className="w-24"
                        data-testid="invoice-tax-rate-input"
                      />
                      <span className="text-[#194a50]/60">% VAT on services</span>
                    </div>
                    <p className="text-xs text-[#194a50]/50 mt-2">
                      Tax applied to agent fee and any deductions from owner
                    </p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong>Owner Payout Formula:</strong><br/>
                      Accommodation - {settings.agent_fee_percentage}% Agent Fee - {settings.invoice_tax_rate}% Tax + Electricity ± Adjustments
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Payout Info */}
          <Card className="shadow-sm border-green-200 bg-green-50 mt-6">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Euro className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-green-800">How owner payouts work</h4>
                  <ul className="text-sm text-green-700 mt-2 space-y-1">
                    <li>• Record check-in and check-out electricity meter readings for each booking</li>
                    <li>• Add any adjustments (deductions for damages, charges for repairs to old items)</li>
                    <li>• Generate owner invoice after checkout with full breakdown</li>
                    <li>• Owner receives: Accommodation - Agent Fee - Tax + Electricity ± Adjustments</li>
                    <li>• Mark invoice as Paid when processed, then Paid Out when transferred to owner</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing Info Tab */}
        <TabsContent value="billing" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Company Details */}
            <Card className="shadow-sm border-[#194a50]/10">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#194a50]" />
                  Company Information
                </CardTitle>
                <CardDescription>
                  Your business details that appear on all invoices
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label>Company Name</Label>
                    <Input
                      type="text"
                      value={settings.company_name}
                      onChange={(e) => setSettings({
                        ...settings,
                        company_name: e.target.value
                      })}
                      placeholder="Your Company Name"
                      className="mt-1"
                      data-testid="company-name-input"
                    />
                  </div>
                  <div>
                    <Label>VAT ID / Tax Number</Label>
                    <Input
                      type="text"
                      value={settings.company_vat_id}
                      onChange={(e) => setSettings({
                        ...settings,
                        company_vat_id: e.target.value
                      })}
                      placeholder="e.g., NL123456789B01"
                      className="mt-1"
                      data-testid="company-vat-input"
                    />
                  </div>
                  <div>
                    <Label>Company Address</Label>
                    <Textarea
                      value={settings.company_address}
                      onChange={(e) => setSettings({
                        ...settings,
                        company_address: e.target.value
                      })}
                      placeholder="Street Address&#10;City, Postal Code&#10;Country"
                      className="mt-1"
                      rows={3}
                      data-testid="company-address-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card className="shadow-sm border-[#194a50]/10">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#194a50]" />
                  Contact Details
                </CardTitle>
                <CardDescription>
                  Contact information for invoices and business correspondence
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      value={settings.company_email}
                      onChange={(e) => setSettings({
                        ...settings,
                        company_email: e.target.value
                      })}
                      placeholder="billing@yourcompany.com"
                      className="mt-1"
                      data-testid="company-email-input"
                    />
                  </div>
                  <div>
                    <Label>Phone Number</Label>
                    <Input
                      type="tel"
                      value={settings.company_phone}
                      onChange={(e) => setSettings({
                        ...settings,
                        company_phone: e.target.value
                      })}
                      placeholder="+31 20 123 4567"
                      className="mt-1"
                      data-testid="company-phone-input"
                    />
                  </div>
                  <div>
                    <Label>Company Logo</Label>
                    <div className="mt-2 space-y-3">
                      {settings.company_logo && (
                        <div className="flex items-center gap-4">
                          <img 
                            src={settings.company_logo} 
                            alt="Company logo" 
                            className="h-16 w-auto object-contain border rounded p-1"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSettings({ ...settings, company_logo: '' })}
                          >
                            Remove
                          </Button>
                        </div>
                      )}
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        id="logo-upload"
                      />
                      <Button
                        variant="outline"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="w-full"
                      >
                        {uploadingLogo ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Image className="w-4 h-4 mr-2" />
                        )}
                        {settings.company_logo ? 'Change Logo' : 'Upload Logo'}
                      </Button>
                      <p className="text-xs text-[#194a50]/50">
                        PNG, JPG or GIF. Max 2MB. Will appear on invoice headers.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Preview Info */}
          <Card className="shadow-sm border-blue-200 bg-blue-50 mt-6">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-800">Invoice Preview</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    These billing details will appear on all owner payout invoices. The logo will be displayed in the header, 
                    followed by company name, address, VAT ID, and contact information.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contracts Tab */}
        <TabsContent value="contracts" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Contract Template Upload */}
            <Card className="shadow-sm border-[#194a50]/10">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#194a50]" />
                  Contract Template
                </CardTitle>
                <CardDescription>
                  Upload a Word document (.docx) with placeholders for booking details
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Current Template Status */}
                  {contractTemplate ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-800">Template Active</span>
                      </div>
                      <p className="text-sm text-green-700">{contractTemplate.filename}</p>
                      <p className="text-xs text-green-600 mt-1">
                        Uploaded: {new Date(contractTemplate.uploaded_at).toLocaleDateString()}
                      </p>
                      {contractTemplate.fields_found?.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs text-green-600 mb-1">Fields detected:</p>
                          <div className="flex flex-wrap gap-1">
                            {contractTemplate.fields_found.map(field => (
                              <Badge key={field} variant="outline" className="text-xs bg-white">
                                {field}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-sm text-amber-800">
                        No contract template uploaded. Customers won't need to sign a contract.
                      </p>
                    </div>
                  )}

                  {/* Upload Button */}
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".docx"
                      onChange={handleContractUpload}
                      className="hidden"
                      id="contract-upload"
                      data-testid="contract-file-input"
                    />
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingContract}
                      data-testid="upload-contract-btn"
                    >
                      {uploadingContract ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          {contractTemplate ? 'Replace Template' : 'Upload Template'}
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-[#194a50]/50 mt-2 text-center">
                      Max file size: 10MB
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Available Placeholders */}
            <Card className="shadow-sm border-[#194a50]/10">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="w-5 h-5 text-[#194a50]" />
                  Available Placeholders
                </CardTitle>
                <CardDescription>
                  Use these placeholders in your Word template
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {contractFields.map((item) => (
                    <div 
                      key={item.field}
                      className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50"
                    >
                      <code className="text-xs bg-[#194a50]/10 text-[#194a50] px-2 py-1 rounded font-mono whitespace-nowrap">
                        {item.field}
                      </code>
                      <span className="text-sm text-[#194a50]/70">{item.description}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contract Info */}
          <Card className="shadow-sm border-blue-200 bg-blue-50 mt-6">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-800">How contract signing works</h4>
                  <ul className="text-sm text-blue-700 mt-2 space-y-1">
                    <li>• Upload a Word document with placeholders like <code className="bg-white/50 px-1 rounded">[TENANT NAME]</code></li>
                    <li>• When customers book, they'll see the contract with their details filled in</li>
                    <li>• Customers must provide their ID number and sign with their full name</li>
                    <li>• The signed contract is stored and can be downloaded anytime</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Info Card */}
      <Card className="shadow-sm border-amber-200 bg-amber-50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Settings className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800">How deposits work</h4>
              <ul className="text-sm text-amber-700 mt-2 space-y-1">
                <li>• <strong>Booking Deposit ({settings.booking_deposit_percentage}%):</strong> Required to confirm reservation. Deducted from final balance.</li>
                <li>• <strong>Security Deposit:</strong> Held separately for damage protection. Returned after checkout if no issues.</li>
                <li>• <strong>Stripe (EU card):</strong> {settings.stripe_eu_fee_percentage}% + €{settings.stripe_fixed_fee} gross-up added to customer charge.</li>
                <li>• <strong>Stripe (International):</strong> {settings.stripe_intl_fee_percentage}% + €{settings.stripe_fixed_fee} gross-up added to customer charge.</li>
                <li>• <strong>Bank transfers:</strong> Pending until admin confirms receipt.</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
